import * as yup from 'yup';
import moment from 'moment';
import { EURActivityFields, EURSMRFields } from './types';

export const EURActivitylInitialValues: EURActivityFields = {
  id: '',
  activity: '',
  startTime: new Date(Date.now()),
  endTime: new Date(Date.now()),
};

export const EURSMRlInitialValues: EURSMRFields = {
  equipmentId: '',
  operatorId: '',
  locationId: '',
  date: new Date(Date.now()),
  downHours: null,
  startingReading: null,
  endingReading: null,
  activities: null,
  startingSmrImage: '',
  endingSmrImage: '',
};

export const EURActivitySchema = yup.object({
  activity: yup.string().required('Activity is required'),
  startTime: yup
  .date()
  .required('Start Time is required')
  .test("is-lesser", "Start time should be less than End Time", function(value) {
    const { endTime } = this.parent;
    return moment(value).isBefore(moment(endTime));
  })
  .typeError("Invalid Time Format"),
  endTime: yup
  .date()
  .required('End Time is required')
  .test("is-greater", "End time should be greater than Start Time", function(value) {
    const { startTime } = this.parent;
    return moment(value).isAfter(moment(startTime));
  })
  .typeError("Invalid Time Format"),
});

export const EURSMRSchema = yup.object({
  equipmentId: yup.string().required('Equipment No. is required'),
  operatorId: yup.string().required('Operator is required'),
  locationId: yup.string().required('Location is required'),
  date: yup.date().required('Date is required').typeError('Invalid Date Format'),
  downHours: yup
  .number()
  .required('Down Hours is required')
  .typeError("Must be a number"),
  startingReading: yup
  .number()
  .required('Starting Reading is required')
  .typeError("Must be a number"),
  endingReading: yup
  .number()
  .nullable()
  .transform((value, originalValue) =>
    originalValue === "" ? null : value
  )
  .typeError("Must be a number")
  .test("ending-pair-reading", function (value) {
    const { endingSmrImage } = this.parent;
    const { hasEndingAlready } = this.options.context || {};

    const hasReading = !!value;
    const hasImage = !!endingSmrImage;

    // If previously saved → cannot remove
    if (hasEndingAlready && !hasReading) {
      return this.createError({
        message: "Ending Reading cannot be removed once set",
      });
    }

    // Pair rule
    if (!hasReading && hasImage) {
      return this.createError({
        message: "Ending Reading is required if image is set",
      });
    }

    return true;
  }),
  startingSmrImage: yup
  .mixed<File | string>()
  .test("required", "Starting SMR Image is required", (value) => {
    return !!value;
  })
  .test("fileType", "Only JPG/PNG allowed", (value) => {
    if (!value || typeof value === "string") return true;
    return ["image/jpeg", "image/png"].includes(value.type);
  })
  .test("fileSize", "Max image size allowed is 5MB", (value) => {
    if (!value || typeof value === "string") return true;
    return value.size <= 5 * 1024 * 1024;
  }),
  endingSmrImage: yup
  .mixed<File | string>()
  .nullable()
  .test("ending-pair-image", function (value) {
    const { endingReading } = this.parent;
    const { hasEndingAlready } = this.options.context || {};

    const hasReading = !!endingReading;
    const hasImage = !!value;

    // If previously saved → cannot remove
    if (hasEndingAlready && !hasImage) {
      return this.createError({
        message: "Ending SMR Image cannot be removed once set",
      });
    }

    // Pair rule
    if (!hasImage && hasReading) {
      return this.createError({
        message: "Ending SMR Image is required if Ending Reading is set",
      });
    }

    return true;
  })
  .test("fileType", "Only JPG/PNG allowed", (value) => {
    if (!value || typeof value === "string") return true;
    return ["image/jpeg", "image/png"].includes(value.type);
  })
  .test("fileSize", "Max image size allowed is 5MB", (value) => {
    if (!value || typeof value === "string") return true;
    return value.size <= 5 * 1024 * 1024;
  }),
});